import React from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useArticle } from '../../hooks/useAirtableArticles';
import blogs from '../../api/blogs';
import BlogSidebar from '../BlogSidebar/BlogSidebar.jsx';
import blog3 from '../../images/blog-details/comments-author/img-1.jpg';
import blog4 from '../../images/blog-details/comments-author/img-2.jpg';
import blog5 from '../../images/blog-details/comments-author/img-3.jpg';
import blog6 from '../../images/blog-details/author.jpg';
import gl1 from '../../images/blog/img-1.jpg';
import gl2 from '../../images/blog/img-2.jpg';

const BlogSingle = (props) => {
    const { slug } = useParams();
    
    // Try to fetch from Airtable first
    const { article: airtableArticle, loading, error } = useArticle(slug);
    
    // Fallback to static blog data
    const staticBlog = blogs.find(item => item.slug === slug);
    
    // Use Airtable article if available, otherwise use static blog
    const article = airtableArticle || staticBlog;
    const isAirtable = !!airtableArticle;

    const submitHandler = (e) => {
        e.preventDefault();
    };

    if (loading) {
        return (
            <section className="wpo-blog-single-section section-padding">
                <div className="container">
                    <div className="row">
                        <div className="col-12 text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading article...</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (!article) {
        return (
            <section className="wpo-blog-single-section section-padding">
                <div className="container">
                    <div className="row">
                        <div className="col-12 text-center">
                            <h2>Article Not Found</h2>
                            <p>The article you're looking for doesn't exist.</p>
                            <Link to="/blog" className="theme-btn mt-3">
                                Back to Blog
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="wpo-blog-single-section section-padding">
            <div className="container">
                <div className="row">
                    <div className={`col col-lg-8 col-12 ${props.blRight}`}>
                        <div className="wpo-blog-content">
                            <div className="post format-standard-image">
                                <div className="entry-meta">
                                    <ul>
                                        <li>
                                            <i className="fi flaticon-user"></i> 
                                            By <Link to={`/blog/${slug}`}>{article.author}</Link>
                                        </li>
                                        {!isAirtable && (
                                            <li>
                                                <i className="fi flaticon-comment-white-oval-bubble"></i> 
                                                Comments {article.comment}
                                            </li>
                                        )}
                                        <li>
                                            <i className="fi flaticon-calendar"></i> 
                                            {isAirtable 
                                                ? new Date(article.publishedDate).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })
                                                : article.create_at
                                            }
                                        </li>
                                        {isAirtable && article.readTime && (
                                            <li>
                                                <i className="fi flaticon-deadline"></i> 
                                                {article.readTime}
                                            </li>
                                        )}
                                    </ul>
                                </div>
                                <h2>{isAirtable ? article.title : article.title2}</h2>
                                
                                {isAirtable ? (
                                    <>
                                        {article.subtitle && (
                                            <h3 className="text-muted mb-4">{article.subtitle}</h3>
                                        )}
                                        
                                        {article.excerpt && (
                                            <p className="lead mb-4">{article.excerpt}</p>
                                        )}
                                        
                                        {article.heroMarkdown && (
                                            <div className="hero-content mb-4">
                                                <ReactMarkdown
                                                    components={{
                                                        h1: ({node, ...props}) => <h3 className="mt-4 mb-3" {...props} />,
                                                        h2: ({node, ...props}) => <h4 className="mt-4 mb-3" {...props} />,
                                                        h3: ({node, ...props}) => <h5 className="mt-3 mb-2" {...props} />,
                                                        p: ({node, ...props}) => <p className="mb-3" {...props} />,
                                                        ul: ({node, ...props}) => <ul className="mb-3" {...props} />,
                                                        ol: ({node, ...props}) => <ol className="mb-3" {...props} />,
                                                        blockquote: ({node, ...props}) => (
                                                            <blockquote {...props} />
                                                        ),
                                                        img: ({src, alt, ...props}) => {
                                                            // Always try to render the image, but hide it if it fails to load
                                                            return (
                                                                <img 
                                                                    className="img-fluid rounded my-4" 
                                                                    src={src}
                                                                    alt={alt || 'Article image'}
                                                                    loading="lazy"
                                                                    onError={(e) => { 
                                                                        // Hide the broken image
                                                                        e.target.style.display = 'none';
                                                                    }}
                                                                    {...props}
                                                                />
                                                            );
                                                        },
                                                        a: ({node, ...props}) => (
                                                            <a target="_blank" rel="noopener noreferrer" {...props} />
                                                        )
                                                    }}
                                                >
                                                    {article.heroMarkdown}
                                                </ReactMarkdown>
                                            </div>
                                        )}
                                        
                                        {article.content && (
                                            <div className="body-content">
                                                <ReactMarkdown
                                                    components={{
                                                        h1: ({node, ...props}) => <h3 className="mt-4 mb-3" {...props} />,
                                                        h2: ({node, ...props}) => <h4 className="mt-4 mb-3" {...props} />,
                                                        h3: ({node, ...props}) => <h5 className="mt-3 mb-2" {...props} />,
                                                        p: ({node, ...props}) => <p className="mb-3" {...props} />,
                                                        ul: ({node, ...props}) => <ul className="mb-3" {...props} />,
                                                        ol: ({node, ...props}) => <ol className="mb-3" {...props} />,
                                                        blockquote: ({node, ...props}) => (
                                                            <blockquote {...props} />
                                                        ),
                                                        img: ({src, alt, ...props}) => {
                                                            // Always try to render the image, but hide it if it fails to load
                                                            return (
                                                                <img 
                                                                    className="img-fluid rounded my-4" 
                                                                    src={src}
                                                                    alt={alt || 'Article image'}
                                                                    loading="lazy"
                                                                    onError={(e) => { 
                                                                        // Hide the broken image
                                                                        e.target.style.display = 'none';
                                                                    }}
                                                                    {...props}
                                                                />
                                                            );
                                                        },
                                                        a: ({node, ...props}) => (
                                                            <a target="_blank" rel="noopener noreferrer" {...props} />
                                                        ),
                                                        code: ({node, inline, ...props}) => 
                                                            inline 
                                                                ? <code className="bg-light px-1 rounded" {...props} />
                                                                : <pre className="bg-light p-3 rounded overflow-auto"><code {...props} /></pre>
                                                    }}
                                                >
                                                    {article.content}
                                                </ReactMarkdown>
                                            </div>
                                        )}
                                        
                                        {/* Only render gallery if images exist and are not empty */}
                                        {article.images && Array.isArray(article.images) && article.images.length > 0 && 
                                         article.images.some(img => img && img.url) && (
                                            <div className="gallery">
                                                {article.images
                                                    .filter(img => img && img.url) // Only include images with valid URLs
                                                    .slice(0, 2) // Show first 2 images in gallery
                                                    .map((image, index) => (
                                                        <div key={index}>
                                                            <img 
                                                                src={image.url} 
                                                                alt={image.alt || `Gallery image ${index + 1}`}
                                                                style={{ width: '100%', height: 'auto', objectFit: 'cover' }}
                                                                onError={(e) => { e.target.style.display = 'none'; }}
                                                            />
                                                        </div>
                                                    ))}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <p>There are many variations of passages of Lorem Ipsum available, but the majority have suffered alteration in some form, by injected humour, or randomised words which don't look even slightly believable. If you are going to use a passage of Lorem Ipsum, you need to be sure there isn't anything embarrassing hidden in the middle of text. All the Lorem Ipsum generators on the Internet tend to repeat predefined chunks as necessary, making this the first true generator on the Internet. It uses a dictionary of over 200 Latin words, combined with a handful.</p>
                                        <blockquote>
                                            Combined with a handful of model sentence structures, generate Lorem Ipsum which looks reasonable. The generated Lorem Ipsum is therefore always free from repetition, injected humour, or non-characteristic words etc.
                                        </blockquote>
                                        <p>I must explain to you how all this mistaken idea of denouncing pleasure and praising pain was born and I will give you a complete account of the system, and expound the actual teachings of the great explorer of the truth, the master-builder of human happiness. No one rejects, dislikes, or avoids pleasure itself,</p>

                                        <div className="gallery">
                                            <div>
                                                <img src={gl1} alt="" />
                                            </div>
                                            <div>
                                                <img src={gl2} alt="" />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="author-box" style={{ marginTop: '50px' }}>
                                <div className="author-avatar">
                                    <Link to="#" target="_blank"><img src="dr-eugene-pereira-picture.jpg" alt="" /></Link>
                                </div>
                                <div className="author-content">
                                    <Link to="#" className="author-name">Author: {article.author}</Link>
                                    <p>
                                        {isAirtable 
                                            ? "Dr. Eugene Pereira is a board-certified pain management specialist with over 20 years of experience in regenerative medicine and interventional pain management."
                                            : "Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis."
                                        }
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <BlogSidebar blLeft={props.blLeft} />
                </div>
            </div>
        </section>
    );
};

export default BlogSingle;